/**
 * Onboarding Component
 *
 * Collects user preferences after registration including family size,
 * dietary preferences, and goals. Updates the user profile via API.
 */

import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import api from '../../services/api';
import './auth.css';

const DIETARY_PREFERENCE_OPTIONS = [
  'Vegetarian',
  'Vegan',
  'Gluten-Free',
  'Dairy-Free',
  'Keto',
  'Paleo',
  'Pescatarian',
  'Halal',
  'Kosher',
];

const DIETARY_PREFERENCE_KEYS: Record<string, string> = {
  'Vegetarian': 'vegetarian',
  'Vegan': 'vegan',
  'Gluten-Free': 'glutenFree',
  'Dairy-Free': 'dairyFree',
  'Keto': 'keto',
  'Paleo': 'paleo',
  'Pescatarian': 'pescatarian',
  'Halal': 'halal',
  'Kosher': 'kosher',
};

export function Onboarding() {
  const navigate = useNavigate();
  const { user, setUser } = useAuthStore();
  const [familySize, setFamilySize] = useState<string>('');
  const [selectedPreferences, setSelectedPreferences] = useState<string[]>([]);
  const [goals, setGoals] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation('auth');

  const handlePreferenceToggle = (preference: string) => {
    setSelectedPreferences((prev) =>
      prev.includes(preference)
        ? prev.filter((p) => p !== preference)
        : [...prev, preference]
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!familySize || parseInt(familySize) < 1) {
      setError(t('invalidFamilySize'));
      return;
    }

    if (!user?.id) {
      setError(t('userNotFound'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.patch(`/users/${user.id}`, {
        user: {
          family_size: parseInt(familySize),
          dietary_preferences: selectedPreferences,
          goals: goals.trim() || null,
        },
      });

      // Update user in auth store with new profile data
      setUser(response.data.user || response.data);

      // Redirect to dashboard or home page
      navigate('/');
    } catch (err) {
      console.error('Onboarding failed:', err);
      setError(
        err instanceof Error ? err.message : t('failedUpdateProfile')
      );
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card" style={{ maxWidth: '540px' }}>
        <h2>{t('onboardingTitle')}</h2>
        <p className="auth-subtitle">
          {t('onboardingSubtitle')}
        </p>

        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          {/* Family Size */}
          <div className="form-group">
            <label htmlFor="family-size">
              {t('familySizeLabel')} <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              id="family-size"
              type="number"
              value={familySize}
              onChange={(e) => setFamilySize(e.target.value)}
              required
              min="1"
              max="20"
              placeholder={t('familySizePlaceholder')}
              disabled={isLoading}
            />
            <small className="form-hint">{t('familySizeHint')}</small>
          </div>

          {/* Dietary Preferences */}
          <div className="form-group">
            <label>{t('dietaryPreferences')}</label>
            <div className="checkbox-group">
              {DIETARY_PREFERENCE_OPTIONS.map((preference) => (
                <label key={preference} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedPreferences.includes(preference)}
                    onChange={() => handlePreferenceToggle(preference)}
                    disabled={isLoading}
                  />
                  <span>{t(DIETARY_PREFERENCE_KEYS[preference] as never)}</span>
                </label>
              ))}
            </div>
            <small className="form-hint">{t('selectAllApply')}</small>
          </div>

          {/* Goals */}
          <div className="form-group">
            <label htmlFor="goals">{t('goalsLabel')}</label>
            <textarea
              id="goals"
              value={goals}
              onChange={(e) => setGoals(e.target.value)}
              placeholder={t('goalsPlaceholder')}
              rows={4}
              disabled={isLoading}
              maxLength={500}
            />
            <small className="form-hint">{t('charCount', { count: goals.length })}</small>
          </div>

          <button type="submit" className="btn-primary" disabled={isLoading}>
            {isLoading ? t('saving') : t('completeSetup')}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            <a href="/" className="auth-link">
              {t('skipForNow')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
